/**
 * Whether a capped decision can be overridden, and what the override
 * jumps (CU-869fdg4nu).
 *
 * Decided by `reason`: every cap that attenuated the shelf price — the
 * policy's rank cap and keep-position guard, the competitor guards, the
 * operator's floors (a date protection included) — plus the markup cap
 * on the conversion. "Para el operador un cap es un cap" (Curro,
 * 2026-10-12, after the first rule left a button nobody could see: the
 * live pass had no floor or markup cap biting at all).
 *
 * The consistency ladder is never jumped: it is not a cap on this
 * price, it is the order between cars, and the engine clamps the
 * override to it instead. Labelled by `capKey`, so a date protection
 * reads "Date protection" and not "Price limit" in the dialog.
 */
import { capKey } from './dateProtection.js'

export const OVERRIDABLE = new Set([
	'rank_cap', 'keep_position',
	'never_below_competitor', 'never_above_competitor',
	'price_limit', 'markup_cap',
])

export const BYPASS_LABELS = {
	rank_cap:               'Rank cap',
	keep_position:          'Keep-position guard',
	never_below_competitor: 'Never below competitor',
	never_above_competitor: 'Never above competitor',
	price_limit:            'Price limit',
	date_protection:        'Date protection',
	markup_cap:             'Markup limit',
}

export function overridable(chain) {
	const caps = Array.isArray(chain) ? chain : []
	const bypasses = caps
		.filter(c => OVERRIDABLE.has(c?.reason))
		.map(c => ({ reason: c.reason, kind: capKey(c), label: c.source?.label || null }))
	// Every layer is the ladder's: there is nothing on this price to
	// jump, and the row says so instead of showing a dash that reads as
	// a missing button.
	const onlyConsistency = caps.length > 0 && bypasses.length === 0
		&& caps.every(c => c?.reason === 'category_consistency')
	return { can: bypasses.length > 0, bypasses, onlyConsistency }
}

/** "Price limit · Date protection (Christmas) · Markup limit" */
export function bypassesSentence(bypasses) {
	return (bypasses || [])
		.map(b => `${BYPASS_LABELS[b.kind] || b.kind}${b.label ? ` (${b.label})` : ''}`)
		.join(' · ')
}
